import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircleIcon, CheckCircle2Icon, InfoIcon, XCircleIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/utils.js';

export const inlineAlertVariants = cva(
  'relative flex w-full items-start gap-3 rounded-md border p-3 text-sm animate-in fade-in-0 duration-[200ms] ease-out',
  {
    variants: {
      variant: {
        info: 'bg-accent/8 border-accent/24 text-foreground [&>svg]:text-accent',
        success: 'bg-success/10 border-success/24 text-foreground [&>svg]:text-success',
        warning: 'bg-warning/10 border-warning/30 text-foreground [&>svg]:text-warning',
        destructive:
          'bg-destructive/10 border-destructive/30 text-foreground [&>svg]:text-destructive',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

const variantIcons = {
  info: InfoIcon,
  success: CheckCircle2Icon,
  warning: AlertCircleIcon,
  destructive: XCircleIcon,
} as const;

export interface InlineAlertProps
  extends ComponentProps<'div'>,
    VariantProps<typeof inlineAlertVariants> {
  readonly title?: string;
  readonly action?: ReactNode;
}

export function InlineAlert({
  variant = 'info',
  title,
  action,
  className,
  children,
  ...props
}: Readonly<InlineAlertProps>) {
  const Icon = variantIcons[variant ?? 'info'];
  return (
    <div
      data-slot="inline-alert"
      role="alert"
      className={cn(inlineAlertVariants({ variant }), className)}
      {...props}
    >
      <Icon className="mt-0.5 size-4 shrink-0" />
      <div className="flex-1 leading-relaxed">
        {title !== undefined && <div className="mb-1 font-medium">{title}</div>}
        {children}
      </div>
      {action !== undefined && <div className="shrink-0">{action}</div>}
    </div>
  );
}
